// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Accordion, Box, Center, Flex, Group, SegmentedControl, Space, Text } from '@mantine/core';
import { FC, useContext } from 'react';
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
  IconChevronRight,
  IconColumns,
  IconDragDrop,
  IconDragDrop2,
  IconPhoto,
  IconPilcrow,
  IconTrash,
} from '@tabler/icons';
import { useParams } from 'react-router-dom';
import { useDrag, useDrop } from 'react-dnd-latest';
import { useElementSize } from '@mantine/hooks';
import { i18n } from '@lingui/core';
import { BlockStyleDto, HorizontalAlignment, TextBlockDto, horizontalAlignmentToCssProp } from '../../Types/DocumentT';
import { openDeleteContractTemplateBlockModal } from '../../Molecules/Settings/ContractTemplates/ContractTemplateModals';
import { isEmpty } from '../../Utils/utils';
import { ContractContext } from '../../Service/Context/ContractContextV2';
import TextBlockFilePreview from '../../Organismns/Contract/TextBlockFilePreview';
import { EventRichInput } from '../../Organismns/tiptap/TiptapEditorInput';
import DocumentInputToolbar from '../../Organismns/tiptap/toolbars/DocumentInputToolbar';
import { ContractBlockButtons } from '../../Organismns/Contract/AddBlockButtons';
import { isDefined } from '../../Service/ValidationS';
import { replacePlaceholdersWithTranslations } from '../../Service/Placeholder/PlaceholderResover';
import DocumentBubbleMenu from '../../Organismns/tiptap/toolbars/BubbleMenu';
import { openDeleteConfirmModal } from '../../Atoms/DeleteConfirmModal';

interface Props {
  index: number;
  textBlock: TextBlockDto;
  isAbove?: boolean;
}

const ContractTextBlock: FC<Props> = ({ index, textBlock, isAbove = false }) => {
  const { id: eventId } = useParams();
  const {
    id,
    headerFirstColumn,
    contentFirstColumn,
    headerSecondColumn,
    contentSecondColumn,
    imageEntityIdFirstColumn,
    imageFileNameFirstColumn,
    styleFirstColumn,
    styleSecondColumn,
  } = textBlock;
  const { templateName } = useParams();
  const { contract, moveBlock, deleteBlock, changeTextBlock, changeBlockStyle } = useContext(ContractContext);

  const { id: firstStylesId, horizontalAlignment: firstAlignment } = styleFirstColumn ?? {};
  const { id: secondStylesId, horizontalAlignment: secondAlignment } = styleSecondColumn ?? {};

  const isFileBlock = isEmpty(headerFirstColumn) && imageEntityIdFirstColumn;
  const columnLayout = contentSecondColumn !== null;

  const changeBlock = (patch: Partial<TextBlockDto>) => changeTextBlock(id, index, isAbove, patch);
  const changeFirstStyle = (patch: Partial<BlockStyleDto>) =>
    changeBlockStyle(firstStylesId, index, isAbove, 'styleFirstColumn', patch);
  const changeSecondStyle = (patch: Partial<BlockStyleDto>) =>
    changeBlockStyle(secondStylesId, index, isAbove, 'styleSecondColumn', patch);

  const { ref, width } = useElementSize();

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'textBlock',
      item: { ...textBlock, isAbove, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [index],
  );

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'textBlock',
      collect: (monitor: any) => ({
        isOver: monitor.isOver() && monitor.getItem().id !== id,
        canDrop: !!monitor.canDrop(),
      }),
      drop(item: any) {
        moveBlock(item.index, index, !item.isAbove, !isAbove);
      },
    }),
    [index],
  );

  return (
    <>
      <Flex ref={ref} key={textBlock.id} align="center" style={{ opacity: isDragging ? 0.5 : 1 }}>
        <div ref={drop} style={{ width: 30 }}>
          <div ref={drag}>
            {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
            {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
          </div>
        </div>
        <Accordion
          defaultValue="foo"
          variant="separated"
          chevronPosition="left"
          chevron={<IconChevronRight size="1rem" />}
          styles={{
            chevron: {
              '&[data-rotate]': {
                transform: 'rotate(90deg)',
              },
            },
          }}
          sx={{ flexGrow: 1 }}
        >
          <Accordion.Item value={isFileBlock ? 'foo1' : 'foo'}>
            <Box sx={{ display: 'flex' }}>
              <Accordion.Control>
                {isFileBlock && (
                  <>
                    <IconPhoto size={20} style={{ marginRight: 10, marginTop: -3 }} />
                    {imageFileNameFirstColumn}{' '}
                  </>
                )}
                {!isFileBlock && (
                  <Text className="overflow-ellipsis" sx={{ width: width - 200 }}>
                    <IconPilcrow size={20} style={{ marginRight: 10, marginTop: -3 }} />
                    {isDefined(headerFirstColumn)
                      ? replacePlaceholdersWithTranslations(i18n, headerFirstColumn)
                      : 'Textblock'}
                  </Text>
                )}
              </Accordion.Control>
              <Group spacing={5} mr={10} position="right" sx={{ width: 100 }}>
                {!columnLayout && (
                  <IconColumns
                    style={{ cursor: 'pointer' }}
                    className="hooverable-icon"
                    size={20}
                    onClick={() => changeBlock({ contentSecondColumn: '' })}
                  />
                )}
                <IconTrash
                  style={{ cursor: 'pointer' }}
                  className="hooverable-icon"
                  size={20}
                  onClick={() => {
                    if (contract) {
                      openDeleteContractTemplateBlockModal({
                        contractId: contract.id,
                        blockId: id,
                        templateName: templateName ?? '',
                        onFinish: () => deleteBlock(index, isAbove),
                      });
                    }
                  }}
                />
              </Group>
            </Box>
            <Accordion.Panel>
              <Group grow sx={{ alignItems: 'flex-start' }}>
                <Box>
                  {firstStylesId && (
                    <Center>
                      <SegmentedControl
                        mb="xs"
                        color="blue"
                        size="xs"
                        value={firstAlignment}
                        onChange={(value: HorizontalAlignment) => changeFirstStyle({ horizontalAlignment: value })}
                        data={[
                          {
                            label: <IconAlignLeft size={20} />,
                            value: 'LEFT',
                          },
                          {
                            label: <IconAlignCenter size={20} />,
                            value: 'CENTER',
                          },
                          {
                            label: <IconAlignRight size={20} />,
                            value: 'RIGHT',
                          },
                          {
                            label: <IconAlignJustified size={20} />,
                            value: 'JUSTIFIED',
                          },
                        ]}
                      />
                    </Center>
                  )}
                  <EventRichInput
                    label="Kopfzeile"
                    value={headerFirstColumn}
                    patchField={(htmlString) => changeBlock({ headerFirstColumn: htmlString })}
                    menu={<DocumentBubbleMenu />}
                    textAlign={horizontalAlignmentToCssProp(firstAlignment)}
                  />
                  <TextBlockFilePreview eventId={eventId} changeBlock={changeBlock} textBlock={textBlock} />
                  <Space h="sm" />
                  <EventRichInput
                    label="Inhalt"
                    value={contentFirstColumn ?? ''}
                    patchField={(htmlString) => changeBlock({ contentFirstColumn: htmlString })}
                    toolbar={<DocumentInputToolbar includeListOpts />}
                    textAlign={horizontalAlignmentToCssProp(firstAlignment)}
                  />
                  {columnLayout && (
                    <Text
                      color="red"
                      variant="link"
                      sx={{ cursor: 'pointer' }}
                      mt={5}
                      onClick={() => {
                        openDeleteConfirmModal({
                          title: 'Text-Block löschen?',
                          description: <Text>Möchtest Du diesen Text-Block wirklich löschen?</Text>,
                          onConfirm: () => changeBlock({ contentFirstColumn: null }),
                        });
                      }}
                    >
                      - Spalte entfernen
                    </Text>
                  )}
                </Box>
                {columnLayout && (
                  <Box>
                    {secondStylesId && (
                      <Center>
                        <SegmentedControl
                          mb="xs"
                          color="blue"
                          size="xs"
                          value={secondAlignment}
                          onChange={(value: HorizontalAlignment) => changeSecondStyle({ horizontalAlignment: value })}
                          data={[
                            {
                              label: <IconAlignLeft size={20} />,
                              value: 'LEFT',
                            },
                            {
                              label: <IconAlignCenter size={20} />,
                              value: 'CENTER',
                            },
                            {
                              label: <IconAlignRight size={20} />,
                              value: 'RIGHT',
                            },
                            {
                              label: <IconAlignJustified size={20} />,
                              value: 'JUSTIFIED',
                            },
                          ]}
                        />
                      </Center>
                    )}
                    <EventRichInput
                      label="Kopfzeile"
                      value={headerSecondColumn}
                      patchField={(htmlString) => changeBlock({ headerSecondColumn: htmlString })}
                      menu={<DocumentBubbleMenu />}
                      textAlign={horizontalAlignmentToCssProp(secondAlignment)}
                    />
                    <TextBlockFilePreview secondary eventId={eventId} changeBlock={changeBlock} textBlock={textBlock} />
                    <Space h="sm" />
                    <EventRichInput
                      label="Inhalt"
                      value={contentSecondColumn}
                      patchField={(htmlString) => changeBlock({ contentSecondColumn: htmlString })}
                      toolbar={<DocumentInputToolbar includeListOpts />}
                      textAlign={horizontalAlignmentToCssProp(secondAlignment)}
                    />
                    {columnLayout && (
                      <Text
                        color="red"
                        variant="link"
                        sx={{ cursor: 'pointer' }}
                        mt={5}
                        onClick={() => {
                          openDeleteConfirmModal({
                            title: 'Text-Block löschen?',
                            description: <Text>Möchtest Du diesen Text-Block wirklich löschen?</Text>,
                            onConfirm: () => changeBlock({ contentSecondColumn: null }),
                          });
                        }}
                      >
                        - Spalte entfernen
                      </Text>
                    )}
                  </Box>
                )}
              </Group>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Flex>
      <ContractBlockButtons id={id} isAbove={isAbove} />
    </>
  );
};

export default ContractTextBlock;
